import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Gauge, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";

const sb = supabase as any;

const LABELS: Record<number, string> = {
  1: "Guessing",
  2: "Unsure",
  3: "Maybe",
  4: "Confident",
  5: "Certain",
};

// Expected accuracy per confidence level for "well-calibrated" learner
const EXPECTED: Record<number, number> = { 1: 20, 2: 40, 3: 60, 4: 80, 5: 95 };

export const CalibrationWidget = () => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["calibration-stats", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: rows } = await sb
        .from("attempts")
        .select("is_correct, confidence")
        .eq("user_id", user!.id)
        .not("confidence", "is", null);

      const total = rows?.length || 0;
      if (total === 0) return { total: 0, buckets: [] as any[], score: null };

      const buckets = [1, 2, 3, 4, 5].map((level) => {
        const sub = (rows || []).filter((r: any) => r.confidence === level);
        const correct = sub.filter((r: any) => r.is_correct).length;
        const accuracy = sub.length > 0 ? Math.round((correct / sub.length) * 100) : 0;
        const expected = EXPECTED[level];
        const gap = sub.length > 0 ? accuracy - expected : 0;
        return { level, count: sub.length, accuracy, expected, gap };
      });

      // Brier-style calibration score: average |actual - expected| across buckets with data
      const active = buckets.filter((b) => b.count > 0);
      const meanGap = active.length > 0
        ? Math.round(active.reduce((s, b) => s + Math.abs(b.gap), 0) / active.length)
        : 0;
      const score = Math.max(0, 100 - meanGap);

      return { total, buckets, score, meanGap };
    },
  });

  if (isLoading) return null;
  if (!data || data.total < 5) {
    return (
      <Card className="medical-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Gauge className="h-5 w-5 text-primary" />
            Confidence Calibration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Rate your confidence on at least 5 questions to unlock calibration insights.
            <span className="block mt-1 text-xs">({data?.total || 0}/5 rated)</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="medical-card border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            Confidence Calibration
          </div>
          <Badge variant="default" className="bg-gradient-medical">
            Score {data.score}/100
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Compares your actual accuracy at each confidence level vs. what a well-calibrated learner achieves.
        </p>

        <div className="space-y-2">
          {data.buckets.map((b: any) => {
            if (b.count === 0) {
              return (
                <div key={b.level} className="flex items-center gap-3 text-sm opacity-50">
                  <span className="w-20 font-medium">{LABELS[b.level]}</span>
                  <span className="text-xs text-muted-foreground">No data yet</span>
                </div>
              );
            }
            const overconfident = b.gap < -10;
            const underconfident = b.gap > 10;
            const calibrated = !overconfident && !underconfident;

            return (
              <div key={b.level} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="font-medium w-20">{LABELS[b.level]}</span>
                    <span className="text-xs text-muted-foreground">({b.count} q{b.count !== 1 ? "s" : ""})</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-bold text-foreground tabular-nums">{b.accuracy}%</span>
                    <span className="text-xs text-muted-foreground tabular-nums">vs {b.expected}%</span>
                    {overconfident && (
                      <Badge variant="outline" className="border-destructive/40 text-destructive text-xs gap-1">
                        <TrendingDown className="h-3 w-3" /> Overconfident
                      </Badge>
                    )}
                    {underconfident && (
                      <Badge variant="outline" className="border-orange-300 text-orange-600 text-xs gap-1">
                        <TrendingUp className="h-3 w-3" /> Underconfident
                      </Badge>
                    )}
                    {calibrated && (
                      <Badge variant="outline" className="border-medical-green/40 text-medical-green text-xs">
                        On target
                      </Badge>
                    )}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden relative">
                  <div className="absolute inset-y-0 left-0 bg-primary/70" style={{ width: `${b.accuracy}%` }} />
                  <div
                    className="absolute inset-y-0 w-0.5 bg-foreground/60"
                    style={{ left: `${b.expected}%` }}
                    title={`Expected ${b.expected}%`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
          <span>
            Tip: A score above 80 means your confidence reliably tracks your knowledge — a key
            skill on high-stakes exams. Bars show actual accuracy; the vertical line marks the expected target.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
